import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import './css/CoverageFigoInfoFee.css';

import { usePetPolicies } from '../../../hooks/usePetPolicies';
import Shimmer from '../../common/Shimmer';
import IconTooltip from '../../common/IconTooltip';
import { CoverageInfoFeeCancelled } from './CoverageInfoFeeCancelled';
import { formatShortDateByLanguage } from '../../../services/language.utils';

import infoIcon from '../../../assets/policy/info-circle.svg';

const CoverageFigoInfoFee = ({
  effectiveDate = '',
  isCancelled = false,
  selectedPolicy = {},
}) => {
  const { t } = useTranslation('insurance');
  const petPolicies = usePetPolicies();
  const { loading } = petPolicies;

  const annualCoverage = useMemo(() => {
    if (selectedPolicy.PolicyDisplayNumber) {
      return selectedPolicy.Benefit.includes('K')
        ? `$${selectedPolicy.Benefit.replace('K', ',000')}`
        : selectedPolicy.Benefit;
    }

    return t('na');
  }, [selectedPolicy]);
  const deductible = selectedPolicy.PolicyDisplayNumber
    ? `$${selectedPolicy.Deductible}` : t('na');
  const reimbursement = selectedPolicy.PolicyDisplayNumber
    ? `${selectedPolicy.Reimbursement}%` : t('na');

  function renderShimmer() {
    return (
      <div className="Flex Flex-column">
        <Shimmer className="Coverage-overview-info-shimmer" visible />

        <Shimmer className="Coverage-overview-info-shimmer" visible />

        <Shimmer className="Coverage-overview-info-shimmer" visible />

        <Shimmer className="Coverage-overview-info-shimmer" visible />
      </div>
    );
  }

  function renderContent() {
    if (isCancelled) {
      const payAmount = selectedPolicy.TotalPremium
        || selectedPolicy.TotalPremium === 0
        ? `$${selectedPolicy.TotalPremium.toFixed(2)}` : t('na');
      const cancellationDate =
        formatShortDateByLanguage(selectedPolicy.CancellationDate);

      return (
        <CoverageInfoFeeCancelled
          cancellationDate={cancellationDate}
          effectiveDate={formatShortDateByLanguage(effectiveDate)}
          payAmount={payAmount}
          policyNumber={selectedPolicy.PolicyDisplayNumber}
        />
      );
    }

    return (
      <>
        <div className="Flex Flex-column">
          <span className="Coverage-overview-info-title">
            {t('policyNumberTitle')}
          </span>

          <span className="Coverage-overview-info-value">
            {selectedPolicy.PolicyDisplayNumber}
          </span>
        </div>

        <div className="Flex Flex-column">
          <span className="Coverage-overview-info-title">
            {t('policyTerm')}
          </span>

          <span className="Coverage-overview-info-value">
            {`${formatShortDateByLanguage(selectedPolicy.PolicyStartDate)} - 
            ${formatShortDateByLanguage(selectedPolicy.PolicyEndDate)}`}
          </span>
        </div>
      </>
    );
  }

  return loading ? renderShimmer() : (
    <>
      <div className={'Coverage-overview-policy-info-container '
        + 'Change-coverate-policy-row'}
      >
        {renderContent()}
      </div>

      {isCancelled ? null : (
        <div className="Coverage-overview-figo-info">
          <div className="Coverage-overview-figo-info-row">
            <div className="Coverage-overview-figo-fee-title">
              <span>{t('annualCoverage')}</span>

              <IconTooltip
                activeOnTouch
                arrowClass="Coverage-overview-tooltip-arrow"
                containerClass="Coverage-overview-tooltip-description"
                description={t('tooltip.annualCoverageTooltip')}
                direction="top-left"
                height={64}
                icon={infoIcon}
                iconClassName="Coverage-overview-tooltip-icon"
                mainClass="Coverage-overview-tooltip-main-class"
                textClassName="Coverage-overview-tooltip-text"
                width={250}
              />
            </div>

            <span className="Coverage-overview-figo-fee-value">
              {annualCoverage}
            </span>
          </div>

          <div className="Coverage-overview-figo-info-row">
            <div className="Coverage-overview-figo-fee-title">
              <span>{t('reimbursement')}</span>

              <IconTooltip
                activeOnTouch
                arrowClass="Coverage-overview-tooltip-arrow"
                containerClass="Coverage-overview-tooltip-description"
                description={t('tooltip.reimbursementTooltip')}
                direction="top-left"
                height={64}
                icon={infoIcon}
                iconClassName="Coverage-overview-tooltip-icon"
                mainClass="Coverage-overview-tooltip-main-class"
                textClassName="Coverage-overview-tooltip-text"
                width={210}
              />
            </div>

            <span className="Coverage-overview-figo-fee-value">
              {reimbursement}
            </span>
          </div>

          <div className="Coverage-overview-figo-info-row">
            <div className="Coverage-overview-figo-fee-title">
              <span>{t('deductible')}</span>

              <IconTooltip
                activeOnTouch
                arrowClass="Coverage-overview-tooltip-arrow"
                containerClass="Coverage-overview-tooltip-description"
                description={t('tooltip.deductibleTooltip')}
                direction="top-left"
                height={64}
                icon={infoIcon}
                iconClassName="Coverage-overview-tooltip-icon"
                mainClass="Coverage-overview-tooltip-main-class"
                textClassName="Coverage-overview-tooltip-text"
                width={240}
              />
            </div>

            <span className="Coverage-overview-figo-fee-value">
              {deductible}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export { CoverageFigoInfoFee };
